import React, { useEffect, useState } from 'react';
import api from '../../api';

/**
 * Format a time slot string (e.g., "21:00") into a readable format (e.g., "9:00pm").
 * Removes leading zeros and converts to 12-hour PM format.
 */
const formatSlotTime = (slot) => {
  // Remove leading zero from hour (e.g., "21:00" -> "9:00")
  const hour = parseInt(slot.split(':')[0], 10);
  const minute = slot.split(':')[1];
  // Convert to 12-hour format with AM/PM
  const hour12 = hour % 12;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour12 === 0 ? '12' : hour12;
  return `${formattedHour}:${minute} ${ampm}`;
};

export default function NavaratriAdminPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const getBookingId = (booking) => {
    if (!booking) return null;
    // The backend returns _id as a string (MongoDB ObjectId).
    // The DELETE endpoint requires a valid ObjectId, so we MUST use the real _id.
    // The constructed fallback ID will NEVER work with the backend's ObjectId validation.
    const id = booking._id || booking.id || booking.booking_id || booking.bookingId;
    if (!id) {
      console.error('Booking missing _id:', booking);
    }
    return id || null;
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const res = await api.get('/api/navaratri/all-bookings');
      setBookings(res.data || []);
    } catch (err) {
      console.error('Failed to load Navaratri bookings:', err);
      setMessage({ type: 'error', text: 'Failed to load bookings' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, []);

  const handleCancel = async (bookingId) => {
    console.log('handleCancel called with bookingId:', bookingId);
    if (!bookingId) {
      setMessage({ type: 'error', text: 'Invalid booking ID' });
      return;
    }
    if (!window.confirm('Cancel this booking? This cannot be undone.')) return;
    try {
      await api.delete(`/api/navaratri/book/${bookingId}`);
      setMessage({ type: 'success', text: 'Booking cancelled successfully.' });
      fetchBookings();
    } catch (err) {
      console.error('Failed to cancel booking:', err);
      setMessage({ type: 'error', text: 'Failed to cancel booking' });
    }
  };

  const handleCancelAll = async () => {
    if (!window.confirm('Cancel ALL Navaratri bookings? This cannot be undone.')) return;
    setCancelling(true);
    setMessage({ type: '', text: '' });
    try {
      const cancelPromises = bookings.map(async (b) => {
        const id = getBookingId(b);
        if (!id) {
          console.warn('Skipping booking without ID:', b);
          return { skipped: true };
        }
        try {
          await api.delete(`/api/navaratri/book/${id}`);
          return { success: true, id };
        } catch (err) {
          console.error(`Failed to cancel booking ${id}:`, err);
          return { success: false, id, error: err.message };
        }
      });
      
      const results = await Promise.all(cancelPromises);
      const failed = results.filter(r => r.success === false);
      const skipped = results.filter(r => r.skipped);
      const succeeded = results.filter(r => r.success === true);
      
      if (failed.length === 0 && skipped.length === 0) {
        setMessage({ type: 'success', text: `All ${succeeded.length} bookings cancelled.` });
      } else {
        setMessage({ 
          type: 'error', 
          text: `Cancelled ${succeeded.length}. Failed: ${failed.length}${skipped.length ? `, Skipped: ${skipped.length}` : ''}.` 
        });
      }
      fetchBookings();
    } catch (err) {
      console.error('Failed to cancel all bookings:', err);
      setMessage({ type: 'error', text: 'Failed to cancel all bookings' });
    } finally {
      setCancelling(false);
    }
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading Navaratri bookings…</div>
      </div>
    );
  }

  return (
    <div className="admin-page navaratri-admin-page">
      <h2 className="admin-page-title">Navaratri Bookings</h2>

      <div className="admin-toolbar">
        <button
          type="button"
          className="button-primary"
          onClick={fetchBookings}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        {bookings.length > 0 && (
          <button
            type="button"
            className="button-danger"
            onClick={handleCancelAll}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling…' : 'Cancel All Bookings'}
          </button>
        )}
      </div>

      {message.text && (
        <div className={`msg msg-${message.type}`} role="alert">
          {message.text}
        </div>
      )}

      {bookings.length === 0 ? (
        <div className="admin-empty">No bookings found.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time Slot</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Booked At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={getBookingId(b)}>
                  <td>{b.date}</td>
                  <td>{formatSlotTime(b.time_slot)}</td>
                  <td>{b.user_name}</td>
                  <td>{b.user_email}</td>
                  <td>{b.user_phone || '—'}</td>
                  <td>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="button-danger button-sm"
                      onClick={() => handleCancel(getBookingId(b))}
                    >
                      Clear Slot
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}